/**
 * Run-queue API client (perf wave 2 — multi-project run queue, 2026-09-07;
 * held entries + history filters 2026-09-08 evening).
 *
 * Talks to the Pipeline backend's `run_queue` blueprint
 * (Backend/Pipeline/routes/run_queue_routes.py, url_prefix /rsa/v2):
 *
 *   POST   /rsa/v2/queue                 enqueue one run payload, or {"runs": [...]};
 *                                        {"hold": true} parks them HELD (never admitted
 *                                        until /start — the admin "Queue" button)
 *   GET    /rsa/v2/queue                 held / pending / active / done + fleet + policy + waiting_reason
 *                                        ?project_ids=1,2 keeps only those projects' entries
 *                                        ?since=today|week|all&mine=<email>&limit=<n> filter the history
 *   DELETE /rsa/v2/queue/<id>            cancel a queued or held entry
 *   POST   /rsa/v2/queue/<id>/priority   {"priority": n} — higher runs first
 *   POST   /rsa/v2/queue/reorder         {"ids": [...]} — this order becomes the admission order
 *                                        (held ids are ordered within the held list)
 *   POST   /rsa/v2/queue/start           {"ids": [...]} — held → queued in that order
 *   POST   /rsa/v2/queue/<id>/requeue    put a cancelled / dispatch-failed entry back
 *   GET    /rsa/v2/fleet                 last controller tick + policy + queue totals
 *                                        ?project_ids=… narrows the per-run parts of the tick
 *
 * AUTHORIZATION (2026-09-17): every call carries the signed-in user's Cognito
 * idToken (authHeaders). The Pipeline verifies it and confines non-admins to the
 * projects they are assigned to; `project_ids` can only narrow that further, and
 * hold / start / reorder / priority are refused for non-admins server-side.
 *
 * The blueprint carries the /rsa prefix itself, so the same path works against
 * localhost:5002 and through CloudFront's /rsa/* behaviour.
 */
import axios from "axios";
import { processApi } from "../API/api";
import { authHeaders } from "../utils/authHeader";
import { projectIdsParam } from "../components/Sidebar/RunQueue/runQueueFormat";

export { projectIdsParam };

export const RUN_QUEUE_BASE = `${processApi}/rsa/v2`;

const JSON_HEADERS = { "Content-Type": "application/json" };

/**
 * Enqueue one run (pass the /process payload object) or many
 * (pass an array of payloads → {"runs": [...]}). `hold: true` stores them
 * HELD (the admin "Queue" action): visible and orderable, never admitted
 * until started from Multi-project runs.
 * Resolves to the 202 body: {queued: [...], errors: [...], totals: {pending, held}}.
 */
export const enqueueRuns = async (runsOrPayload, { priority, hold } = {}) => {
  const body = Array.isArray(runsOrPayload)
    ? { runs: runsOrPayload }
    : { ...runsOrPayload };
  if (priority != null) body.priority = priority;
  if (hold) body.hold = true;
  const response = await axios.post(`${RUN_QUEUE_BASE}/queue`, body, {
    headers: await authHeaders(JSON_HEADERS),
  });
  return response.data;
};

export const fetchQueue = async ({
  includeDone = true,
  userEmail = null,
  projectId = null,
  projectIds = null,
  since = null,
  mine = null,
  limit = null,
} = {}) => {
  const params = new URLSearchParams();
  if (includeDone) params.set("include_done", "1");
  if (userEmail) params.set("user_email", userEmail);
  if (projectId != null && projectId !== "") params.set("project_id", String(projectId));
  const scoped = projectIdsParam(projectIds);
  if (scoped) params.set("project_ids", scoped);
  if (since) params.set("since", String(since));
  if (mine) params.set("mine", String(mine));
  if (limit != null && Number.isFinite(Number(limit))) params.set("limit", String(limit));
  const qs = params.toString();
  const response = await axios.get(`${RUN_QUEUE_BASE}/queue${qs ? `?${qs}` : ""}`, {
    headers: await authHeaders(),
  });
  return response.data;
};

export const fetchFleet = async ({ projectIds = null } = {}) => {
  const scoped = projectIdsParam(projectIds);
  const qs = scoped ? `?project_ids=${encodeURIComponent(scoped)}` : "";
  const response = await axios.get(`${RUN_QUEUE_BASE}/fleet${qs}`, { headers: await authHeaders() });
  return response.data;
};

export const cancelQueued = async (entryId) => {
  const response = await axios.delete(
    `${RUN_QUEUE_BASE}/queue/${encodeURIComponent(entryId)}`,
    { headers: await authHeaders() },
  );
  return response.data;
};

export const setQueuedPriority = async (entryId, priority) => {
  const response = await axios.post(
    `${RUN_QUEUE_BASE}/queue/${encodeURIComponent(entryId)}/priority`,
    { priority: Number(priority) },
    { headers: await authHeaders(JSON_HEADERS) },
  );
  return response.data;
};

export const requeueEntry = async (entryId) => {
  const response = await axios.post(
    `${RUN_QUEUE_BASE}/queue/${encodeURIComponent(entryId)}/requeue`,
    {},
    { headers: await authHeaders(JSON_HEADERS) },
  );
  return response.data;
};

/**
 * Make `ids` (queue entry ids, first = next to start) the admission order in
 * one call. Held ids are ordered within the held list. Resolves to
 * {reordered: [...], ignored: [...], pending: [...], held: [...]}.
 */
export const reorderQueue = async (ids) => {
  const response = await axios.post(
    `${RUN_QUEUE_BASE}/queue/reorder`,
    { ids: (ids || []).map(String) },
    { headers: await authHeaders(JSON_HEADERS) },
  );
  return response.data;
};

/**
 * Start held entries: `ids` (first = next to start) become queued in that
 * order and the controller admits them as capacity allows. Resolves to
 * {started: [...], reordered: [...], ignored: [...], pending: [...], held: [...]}.
 */
export const startQueued = async (ids) => {
  const response = await axios.post(
    `${RUN_QUEUE_BASE}/queue/start`,
    { ids: (ids || []).map(String) },
    { headers: await authHeaders(JSON_HEADERS) },
  );
  return response.data;
};

const runQueueService = {
  enqueueRuns,
  fetchQueue,
  fetchFleet,
  cancelQueued,
  setQueuedPriority,
  reorderQueue,
  startQueued,
  requeueEntry,
};

export default runQueueService;
